import React, { useState } from "react";
import InstaColors, { useSharedValue } from "../components/Constants/constants";
import { headerStyle, sectionStyle } from "./formConstants";
import { FormDate, FormInput, FormTitle, createRequiredLabel } from "./common";
import "../Forms/style.css";
import CountryDropdown from "./CountryDropdown";
import { W8BENFormModel } from "../Models/W8BENModels";
import { useW8BENForm } from "../Hooks/useW8BENForm";
import { W8BENService } from "../services/W8BENService";
import { validateTINFormat } from "../utils/formUtils";

// Add utility function for address parsing
const parseAddressComponents = (addressString: string) => {
  if (!addressString) return { city: "", state: "", postalCode: "" };

  // Split by comma and trim whitespace
  const parts = addressString.split(",").map((part) => part.trim());

  // Initialize result object
  const result = {
    city: "",
    state: "",
    postalCode: "",
  };

  // If we have at least one part, it's the city
  if (parts.length > 0) {
    result.city = parts[0];
  }

  // If we have a second part, it's the state
  if (parts.length > 1) {
    result.state = parts[1];
  }

  // If we have a third part, it's the postal code
  if (parts.length > 2) {
    result.postalCode = parts[2];
  }

  return result;
};

export class W8BENFormProps {
  selectedRowData?: any;
  row?: any;
  selectedIndex?: number;
  onSaveSuccess?: (row: any) => void;
}

const W8BENFORM = ({ selectedRowData, row, selectedIndex = 0, onSaveSuccess }: W8BENFormProps) => {
  const { isMenuOpen } = useSharedValue();

  // Use W8BEN form hook for state management
  const {
    model,
    isLoading,
    isSaving,
    errors,
    isValid,
    updateField,
    updateMultipleFields,
    validate,
  } = useW8BENForm({
    initialData: selectedRowData,
  });

  // Legacy form data state for UI compatibility
  const [formData, setFormData] = useState(() => {
    // Convert model to form data format for legacy UI
    const modelData = model as any;
    return {
      ...modelData,
    };
  });

  // Update formData whenever model changes
  React.useEffect(() => {
    setFormData({ ...model });
  }, [model]);

  // Watch for changes in selectedRowData and update form fields accordingly
  React.useEffect(() => {
    if (selectedRowData) {
      // Check if UniqueIdentifier has changed and update it
      const newUniqueIdentifier =
        selectedRowData.uniqueidentifier || selectedRowData.UniqueIdentifier;
      const currentUniqueIdentifier = formData.UniqueIdentifier;

      if (newUniqueIdentifier && newUniqueIdentifier !== currentUniqueIdentifier) {
        console.log("Updating UniqueIdentifier in form:", {
          from: currentUniqueIdentifier,
          to: newUniqueIdentifier,
        });
        updateField("UniqueIdentifier", newUniqueIdentifier);
      }

      // Update other fields that might have changed
      Object.keys(selectedRowData).forEach((key) => {
        const value = selectedRowData[key];
        const currentValue = (formData as any)[key];

        // Only update if the value has actually changed and is not empty
        if (value && value !== currentValue && typeof value === "string") {
          updateField(key, value);
        }
      });
    }
  }, [selectedRowData, updateField]);

  // Auto-detect and set Taxpayer type based on TIN value from API
  React.useEffect(() => {
    const tinValue = formData["U.S. taxpayer identification number"];
    const currentTaxpayerType = formData["Taxpayer type"];

    if (tinValue && tinValue.trim() !== "") {
      const trimmedTin = tinValue.trim();

      // W-8BEN is for individuals only - validate accordingly
      const validation = validateTINFormat(trimmedTin, currentTaxpayerType || "SSN/ITIN");

      // Special handling for W-8BEN: Only allow SSN/ITIN format
      if (validation.detectedType === "EIN") {
        setTinValidationMessage(
          "⚠️ EIN format detected. W-8BEN is for individuals only. For entities, please use W-8BEN-E form with EIN (XX-XXXXXXX) format."
        );
      } else {
        // Set validation message for SSN/ITIN
        setTinValidationMessage(validation.message);

        // If type was detected and it's different from current, update it
        if (validation.detectedType && validation.detectedType !== currentTaxpayerType) {
          console.log("🔧 Auto-detecting Taxpayer type based on TIN from API:", {
            tin: trimmedTin,
            detected: validation.detectedType,
            current: currentTaxpayerType,
          });

          // Map detected type to form values
          let formType = validation.detectedType;
          if (validation.detectedType === "SSN" || validation.detectedType === "ITIN") {
            formType = "SSN/ITIN"; // W-8BEN uses combined SSN/ITIN option
          }

          updateField("Taxpayer type", formType);
        }
        // If no type was detected, show warning but don't update type
        else if (!validation.detectedType && validation.message) {
          console.log("⚠️ TIN from API could not be auto-detected:", {
            tin: trimmedTin,
            message: validation.message,
          });
        }
      }
    } else {
      // Clear validation message when TIN is empty
      setTinValidationMessage(null);
    }
  }, [formData["U.S. taxpayer identification number"], formData["Taxpayer type"], updateField]);

  // Update treaty claims when model changes
  React.useEffect(() => {
    const extractedClaims: any[] = [];
    const data = model as any;

    // Debug: Log available treaty fields for development
    const treatyFields = Object.keys(data).filter((key) => key.includes("Treaty"));
    if (treatyFields.length > 0) {
      console.log("Treaty fields found:", treatyFields.length);
    }

    // Look for treaty fields with indices
    let claimIndex = 0;
    while (true) {
      const hasClaimData =
        data[`W8BEN-Treaty_country_${claimIndex}`] ||
        data[`W8BEN-Treaty_article_${claimIndex}`] ||
        data[`W8BEN-Treaty_withholding_rate_${claimIndex}`] ||
        data[`W8BEN-Treaty_income_type_${claimIndex}`] ||
        data[`W8BEN-Treaty_conditions_explanation_${claimIndex}`];

      if (!hasClaimData && claimIndex === 0) {
        // Check for legacy fields without index (for the first claim)
        const hasLegacyData =
          data["W8BEN-Treaty_country"] ||
          data["W8BEN-Treaty_article"] ||
          data["W8BEN-Treaty_withholding_rate"] ||
          data["W8BEN-Treaty_income_type"] ||
          data["W8BEN-Treaty_conditions_explanation"];

        if (hasLegacyData) {
          extractedClaims.push({
            country: data["W8BEN-Treaty_country"] || "",
            other: data["Treaty_other_0"] || "",
            article: data["W8BEN-Treaty_article"] || "",
            paragraph: data["Treaty_paragraph_0"] || "",
            subparagraph: data["Treaty_subparagraph_0"] || "",
            percentage: data["W8BEN-Treaty_withholding_rate"] || "",
            typeOfIncome: data["W8BEN-Treaty_income_type"] || "",
            explanation: data["W8BEN-Treaty_conditions_explanation"] || "",
          });
          claimIndex++;
          continue;
        }
      }

      if (!hasClaimData) break;

      extractedClaims.push({
        country: data[`W8BEN-Treaty_country_${claimIndex}`] || "",
        other: data[`Treaty_other_${claimIndex}`] || "",
        article: data[`W8BEN-Treaty_article_${claimIndex}`] || "",
        paragraph: data[`Treaty_paragraph_${claimIndex}`] || "",
        subparagraph: data[`Treaty_subparagraph_${claimIndex}`] || "",
        percentage: data[`W8BEN-Treaty_withholding_rate_${claimIndex}`] || "",
        typeOfIncome: data[`W8BEN-Treaty_income_type_${claimIndex}`] || "",
        explanation: data[`W8BEN-Treaty_conditions_explanation_${claimIndex}`] || "",
      });

      claimIndex++;
    }

    if (extractedClaims.length > 0) {
      setTreatyClaims(extractedClaims);
    }
  }, [model]);

  // Treaty claims state for dynamic treaty sections
  const emptyTreatyClaim = {
    country: "",
    other: "",
    article: "",
    paragraph: "",
    subparagraph: "",
    percentage: "",
    typeOfIncome: "",
    explanation: "",
  };

  // State to control validation summary display
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // State for TIN validation message
  const [tinValidationMessage, setTinValidationMessage] = useState<string | null>(null);

  const [treatyClaims, setTreatyClaims] = useState<any[]>(() => {
    // Initialize treaty claims from model if available
    if (selectedRowData && selectedRowData.treatyClaims) {
      try {
        return typeof selectedRowData.treatyClaims === "string"
          ? JSON.parse(selectedRowData.treatyClaims)
          : selectedRowData.treatyClaims;
      } catch (error) {
        console.error("Error parsing treaty claims:", error);
      }
    }

    // Also check for individual treaty fields from model/selectedRowData
    const extractedClaims: any[] = [];
    const data = selectedRowData || {};

    // Look for treaty fields with indices (W8BEN-Treaty_country_0, W8BEN-Treaty_country_1, etc.)
    let claimIndex = 0;
    while (true) {
      const hasClaimData =
        data[`W8BEN-Treaty_country_${claimIndex}`] ||
        data[`W8BEN-Treaty_article_${claimIndex}`] ||
        data[`W8BEN-Treaty_withholding_rate_${claimIndex}`] ||
        data[`W8BEN-Treaty_income_type_${claimIndex}`] ||
        data[`W8BEN-Treaty_conditions_explanation_${claimIndex}`];

      if (!hasClaimData && claimIndex === 0) {
        // Check for legacy fields without index (for the first claim)
        const hasLegacyData =
          data["W8BEN-Treaty_country"] ||
          data["W8BEN-Treaty_article"] ||
          data["W8BEN-Treaty_withholding_rate"] ||
          data["W8BEN-Treaty_income_type"] ||
          data["W8BEN-Treaty_conditions_explanation"];

        if (hasLegacyData) {
          extractedClaims.push({
            country: data["W8BEN-Treaty_country"] || "",
            other: data["Treaty_other_0"] || "",
            article: data["W8BEN-Treaty_article"] || "",
            paragraph: data["Treaty_paragraph_0"] || "",
            subparagraph: data["Treaty_subparagraph_0"] || "",
            percentage: data["W8BEN-Treaty_withholding_rate"] || "",
            typeOfIncome: data["W8BEN-Treaty_income_type"] || "",
            explanation: data["W8BEN-Treaty_conditions_explanation"] || "",
          });
          claimIndex++;
          continue;
        }
      }

      if (!hasClaimData) break;

      extractedClaims.push({
        country: data[`W8BEN-Treaty_country_${claimIndex}`] || "",
        other: data[`Treaty_other_${claimIndex}`] || "",
        article: data[`W8BEN-Treaty_article_${claimIndex}`] || "",
        paragraph: data[`Treaty_paragraph_${claimIndex}`] || "",
        subparagraph: data[`Treaty_subparagraph_${claimIndex}`] || "",
        percentage: data[`W8BEN-Treaty_withholding_rate_${claimIndex}`] || "",
        typeOfIncome: data[`W8BEN-Treaty_income_type_${claimIndex}`] || "",
        explanation: data[`W8BEN-Treaty_conditions_explanation_${claimIndex}`] || "",
      });

      claimIndex++;
    }

    return extractedClaims.length > 0 ? extractedClaims : [{ ...emptyTreatyClaim }];
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type, checked, value } = e.target;

    // Handle Taxpayer type dropdown change
    if (name === "Taxpayer type") {
      // When type changes, clear the TIN input so user can enter new value
      updateField(name, value);
      updateField("U.S. taxpayer identification number", "");

      // Clear validation message
      setTinValidationMessage("");

      return;
    }

    // Handle TIN validation and auto-detection
    if (name === "U.S. taxpayer identification number") {
      const tinValue = value.toString().trim();
      const currentType = formData["Taxpayer type"];

      if (tinValue) {
        // W-8BEN is for individuals only - validate accordingly
        const validation = validateTINFormat(tinValue, currentType || "SSN/ITIN");

        // Special handling for W-8BEN: Only allow SSN/ITIN format
        if (validation.detectedType === "EIN") {
          setTinValidationMessage(
            "⚠️ EIN format detected. W-8BEN is for individuals only. For entities, please use W-8BEN-E form with EIN (XX-XXXXXXX) format."
          );
        } else {
          // Set validation message for SSN/ITIN
          setTinValidationMessage(validation.message);

          // Auto-select SSN/ITIN if valid and no type selected
          if (
            validation.isValid &&
            (!currentType || currentType === "" || currentType === "---Select---")
          ) {
            updateField("Taxpayer type", "SSN/ITIN");
          }
        }
      } else {
        setTinValidationMessage(null);
      }
    }

    // Update the model through the hook
    updateField(name, type === "checkbox" ? checked : value);

    // Handle address component changes
    if (name === "Permanent city address") {
      const address = parseAddressComponents(value);
      updateMultipleFields({
        "Permanent city address": value,
        "Permanent address city": address.city,
        "Permanent address state NonUS": address.state,
        "Permanent address zip": address.postalCode,
      });
    } else if (name === "Mailing address city") {
      const address = parseAddressComponents(value);
      updateMultipleFields({
        "Mailing city address": address.city,
        "Mailing address state NonUS": address.state,
        "Mailing address zip": address.postalCode,
      });
    } else if (name === "Permanent residence address") {
      updateMultipleFields({
        "Permanent residence address": value,
      });
    } else if (name === "Mailing address") {
      updateMultipleFields({
        "Mailing address": value,
      });
    }
  };

  const handleTreatyClaimChange = (
    idx: number,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    setTreatyClaims((prev) =>
      prev.map((claim, i) => {
        if (i === idx) {
          return { ...claim, [name]: value };
        }
        return claim;
      })
    );

    // Also update the model with the W8BEN-* prefixed fields
    const updates: any = {};
    updates[`W8BEN-Treaty_country_${idx}`] =
      name === "country" ? value : treatyClaims[idx]?.country || "";
    updates[`W8BEN-Treaty_article_${idx}`] =
      name === "article" ? value : treatyClaims[idx]?.article || "";
    updates[`W8BEN-Treaty_withholding_rate_${idx}`] =
      name === "percentage" ? value : treatyClaims[idx]?.percentage || "";
    updates[`W8BEN-Treaty_income_type_${idx}`] =
      name === "typeOfIncome" ? value : treatyClaims[idx]?.typeOfIncome || "";
    updates[`W8BEN-Treaty_conditions_explanation_${idx}`] =
      name === "explanation" ? value : treatyClaims[idx]?.explanation || "";
    updates[`Treaty_paragraph_${idx}`] =
      name === "paragraph" ? value : treatyClaims[idx]?.paragraph || "";
    updates[`Treaty_subparagraph_${idx}`] =
      name === "subparagraph" ? value : treatyClaims[idx]?.subparagraph || "";
    updates[`Treaty_other_${idx}`] = name === "other" ? value : treatyClaims[idx]?.other || "";

    updateMultipleFields(updates);
  };

  const handleAddTreatyClaim = () => {
    setTreatyClaims([...treatyClaims, { ...emptyTreatyClaim }]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      console.error("Form validation failed");
      // Show validation summary
      setShowValidationErrors(true);
      // Show validation error alert
      const errorMessages = Object.values(errors).join("\n");
      alert(`Please fix the following errors before submitting:\n\n${errorMessages}`);
      return;
    }

    // Set saving state manually since we're not using the hook's save method
    // setIsSaving(true); // We can't access this from the hook

    try {
      // Use the model directly as it contains the most up-to-date values
      const currentFormValues: any = { ...model };

      // Add treaty claims data
      const treatyClaimsString = JSON.stringify(treatyClaims);
      currentFormValues.treatyClaims = treatyClaimsString;

      // Set mapping as done to prevent re-mapping on future loads
      currentFormValues.isMapDone = true;

      // Add individual treaty fields
      treatyClaims.forEach((claim, idx) => {
        currentFormValues[`W8BEN-Treaty_country_${idx}`] = claim.country || "";
        currentFormValues[`W8BEN-Treaty_article_${idx}`] = claim.article || "";
        currentFormValues[`W8BEN-Treaty_withholding_rate_${idx}`] = claim.percentage || "";
        currentFormValues[`W8BEN-Treaty_income_type_${idx}`] = claim.typeOfIncome || "";
        currentFormValues[`W8BEN-Treaty_conditions_explanation_${idx}`] = claim.explanation || "";
        currentFormValues[`Treaty_other_${idx}`] = claim.other || "";
        currentFormValues[`Treaty_paragraph_${idx}`] = claim.paragraph || "";
        currentFormValues[`Treaty_subparagraph_${idx}`] = claim.subparagraph || "";
      });

      // Also set legacy treaty fields for first claim (for backward compatibility)
      if (treatyClaims.length > 0) {
        currentFormValues["W8BEN-Treaty_country"] = treatyClaims[0].country || "";
        currentFormValues["W8BEN-Treaty_article"] = treatyClaims[0].article || "";
        currentFormValues["W8BEN-Treaty_withholding_rate"] = treatyClaims[0].percentage || "";
        currentFormValues["W8BEN-Treaty_income_type"] = treatyClaims[0].typeOfIncome || "";
        currentFormValues["W8BEN-Treaty_conditions_explanation"] =
          treatyClaims[0].explanation || "";
      }

      // Final cleanup: Remove unwanted fields right before saving
      currentFormValues["Permanent city address"] = "";
      currentFormValues["Mailing address city"] = "";
      delete currentFormValues["W8BEN-Treaty_country"];
      delete currentFormValues["W8BEN-Treaty_article"];
      delete currentFormValues["W8BEN-Treaty_withholding_rate"];
      delete currentFormValues["W8BEN-Treaty_income_type"];
      delete currentFormValues["W8BEN-Treaty_conditions_explanation"];
      delete currentFormValues["Beneficial Owner name"];
      console.log(currentFormValues);

      // Create a new model with the current form values
      const saveModel = new W8BENFormModel(currentFormValues);

      // Save directly using the service with our collected current values
      const success = await W8BENService.saveToApi(saveModel, row, selectedIndex);

      if (success) {
        // Clear validation errors on successful save
        setShowValidationErrors(false);

        if (onSaveSuccess) {
          onSaveSuccess(row);
        }
        alert("Form saved successfully");
      }
    } catch (error) {
      console.error("Failed to save W-8BEN form:", error);
      alert("Failed to save form data");
    } finally {
      // Reset saving state manually
      // setIsSaving(false); // We can't access this from the hook
    }
  };

  return (
    <div className="scroll-container" style={headerStyle(isMenuOpen)}>
      <div
        style={{ width: "100%", display: "flex", flexDirection: "column", padding: 0, margin: 0 }}
      >
        <form
          onSubmit={handleSubmit}
          style={{
            width: "100% !important",
            minWidth: "100%",
            maxWidth: "100%",
            flex: "1 1 100%",
            display: "flex !important",
            flexDirection: "column",
            margin: "0 !important",
            padding: "0 !important",
            boxSizing: "border-box",
          }}
        >
          <div style={{ display: "flex", width: "100%", flexDirection: "column" }}>
            {/* Validation Summary - Only show after user tries to submit */}
            {showValidationErrors && Object.keys(errors).length > 0 && (
              <div
                style={{
                  backgroundColor: "#fee2e2",
                  border: "1px solid #fecaca",
                  borderRadius: "6px",
                  padding: "12px",
                  margin: "16px 0",
                  color: "#b91c1c",
                }}
              >
                <h4 style={{ margin: "0 0 8px 0", color: "#991b1b" }}>
                  Please fix the following errors:
                </h4>
                <ul style={{ margin: 0, paddingLeft: "20px" }}>
                  {Object.entries(errors).map(([field, error], index) => (
                    <li key={index} style={{ marginBottom: "4px" }}>
                      <strong>{field.replace("W8BEN-", "").replace(/_/g, " ")}:</strong> {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Unique Identifier - First Field */}
            <section style={sectionStyle}>
              <FormInput
                label={createRequiredLabel("Unique Form Identifier")}
                id={"UniqueIdentifier"}
                name={"UniqueIdentifier"}
                value={(formData["UniqueIdentifier"] as string) || ""}
                onChange={handleChange}
              />
            </section>

            {/* Part I: Identification of Beneficial Owner */}
            <section style={sectionStyle}>
              <FormTitle value={"Part I Identification of Beneficial Owner"} />

              <div>
                <FormInput
                  label={createRequiredLabel("First name and middle name")}
                  id={"First name and middle name"}
                  name={"First name and middle name"}
                  value={(formData["First name and middle name"] as string) || ""}
                  onChange={handleChange}
                />
                {showValidationErrors && errors["First name and middle name"] && (
                  <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                    {errors["First name and middle name"]}
                  </div>
                )}
              </div>

              <div>
                <FormInput
                  label={createRequiredLabel("Last name")}
                  id={"Last name"}
                  name={"Last name"}
                  value={(formData["Last name"] as string) || ""}
                  onChange={handleChange}
                />
                {showValidationErrors && errors["Last name"] && (
                  <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                    {errors["Last name"]}
                  </div>
                )}
              </div>

              <div style={{ display: "flex", flexDirection: "column", marginBottom: 16 }}>
                <FormDate
                  label={"Date of birth:"}
                  name={"Date of birth"}
                  value={(formData["Date of birth"] as string) || ""}
                  onChange={handleChange}
                />
                <span style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                  (Enter in U.S. date format: mm/dd/yyyy)
                </span>
                {showValidationErrors && errors["Date of birth"] && (
                  <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                    {errors["Date of birth"]}
                  </div>
                )}
              </div>

              <div>
                <CountryDropdown
                  label="Country of citizenship:"
                  value={
                    typeof formData["Country of citizenship"] === "string"
                      ? formData["Country of citizenship"]
                      : ""
                  }
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "Country of citizenship",
                        value: e.target.value,
                        type: "select-one",
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                />
                {showValidationErrors && errors["Country of citizenship"] && (
                  <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                    {errors["Country of citizenship"]}
                  </div>
                )}
              </div>

              <FormInput
                label={"Other:"}
                id={"Other"}
                name={"Other"}
                value={(formData["Other"] as string) || ""}
                onChange={handleChange}
              />
            </section>

            {/* Permanent address section */}
            <section style={sectionStyle}>
              <FormTitle value={"Permanent address"} />
              <div>
                <FormInput
                  label={"Line 1:"}
                  id={"Permanent residence address"}
                  name={"Permanent residence address"}
                  value={(formData["Permanent residence address"] as string) || ""}
                  onChange={handleChange}
                />
                {showValidationErrors && errors["Permanent residence address"] && (
                  <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                    {errors["Permanent residence address"]}
                  </div>
                )}
              </div>
              <FormInput
                label={"Line 2:"}
                id={"Permanent address line 2"}
                name={"Permanent address line 2"}
                value={(formData["Permanent address line 2"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 3:"}
                id={"Permanent address line 3"}
                name={"Permanent address line 3"}
                value={(formData["Permanent address line 3"] as string) || ""}
                onChange={handleChange}
              />
              <div>
                <FormInput
                  label={"City / Town:"}
                  id={"Permanent address city"}
                  name={"Permanent address city"}
                  value={(formData["Permanent address city"] as string) || ""}
                  onChange={handleChange}
                />
                {showValidationErrors && errors["Permanent address city"] && (
                  <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                    {errors["Permanent address city"]}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 16, alignItems: "flex-end", marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 500 }}>
                    <span style={{ fontWeight: 700 }}>U.S.& Canada:</span> State / Province:
                    <span
                      style={{ color: "#fbbf24", marginLeft: 4, fontSize: 16 }}
                      title="For U.S. and Canada addresses only"
                    >
                      &#9432;
                    </span>
                  </label>
                  <FormInput
                    label={""}
                    id={"Permanent address state USCA"}
                    name={"Permanent address state USCA"}
                    value={(formData["Permanent address state USCA"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 500 }}>
                    <span style={{ fontWeight: 700 }}>Non U.S.:</span> State / Province as it
                    appears on form:
                  </label>
                  <FormInput
                    label={""}
                    id={"Permanent address state NonUS"}
                    name={"Permanent address state NonUS"}
                    value={(formData["Permanent address state NonUS"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <FormInput
                label={"Zip / Postal Code:"}
                id={"Permanent address zip"}
                name={"Permanent address zip"}
                value={(formData["Permanent address zip"] as string) || ""}
                onChange={handleChange}
              />
              <div>
                <CountryDropdown
                  label="Country: *"
                  name="Permanent address country"
                  id="Permanent address country"
                  value={
                    typeof formData["Permanent address country"] === "string"
                      ? formData["Permanent address country"]
                      : ""
                  }
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "Permanent address country",
                        value: e.target.value,
                        type: "select-one",
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                />
                {showValidationErrors && errors["Permanent address country"] && (
                  <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                    {errors["Permanent address country"]}
                  </div>
                )}
              </div>
              <FormInput
                label={"Other:"}
                id={"Other"}
                name={"Other"}
                value={(formData["Other"] as string) || ""}
                onChange={handleChange}
              />
              {/* Yes/No radios */}
              <div style={{ marginTop: 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500 }}>
                    Is care-of-address: <span style={{ color: "red" }}>*</span>
                  </label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Permanent address is care of"
                      value="yes"
                      checked={formData["Permanent address is care of"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Permanent address is care of"
                      value="no"
                      checked={formData["Permanent address is care of"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                  {showValidationErrors && errors["Permanent address is care of"] && (
                    <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                      {errors["Permanent address is care of"]}
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500 }}>
                    Is P.O. Box: <span style={{ color: "red" }}>*</span>
                  </label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Permanent address is po box"
                      value="yes"
                      checked={formData["Permanent address is po box"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Permanent address is po box"
                      value="no"
                      checked={formData["Permanent address is po box"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                  {showValidationErrors && errors["Permanent address is po box"] && (
                    <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                      {errors["Permanent address is po box"]}
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500 }}>
                    Is rural route: <span style={{ color: "red" }}>*</span>
                  </label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Permanent address is rural route"
                      value="yes"
                      checked={formData["Permanent address is rural route"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Permanent address is rural route"
                      value="no"
                      checked={formData["Permanent address is rural route"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                  {showValidationErrors && errors["Permanent address is rural route"] && (
                    <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                      {errors["Permanent address is rural route"]}
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Mailing address section */}
            <section style={sectionStyle}>
              <FormTitle value={"Mailing address"} />
              <FormInput
                label={"Line 1:"}
                id={"Mailing address"}
                name={"Mailing address"}
                value={(formData["Mailing address"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 2:"}
                id={"Mailing address line 2"}
                name={"Mailing address line 2"}
                value={(formData["Mailing address line 2"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 3:"}
                id={"Mailing address line 3"}
                name={"Mailing address line 3"}
                value={(formData["Mailing address line 3"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"City / Town:"}
                id={"Mailing city address"}
                name={"Mailing city address"}
                value={(formData["Mailing city address"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ display: "flex", gap: 16, alignItems: "flex-end", marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 500 }}>
                    <span style={{ fontWeight: 700 }}>U.S.& Canada:</span> State / Province:
                    <span
                      style={{ color: "#fbbf24", marginLeft: 4, fontSize: 16 }}
                      title="For U.S. and Canada addresses only"
                    >
                      &#9432;
                    </span>
                  </label>
                  <FormInput
                    label={""}
                    id={"Mailing address state USCA"}
                    name={"Mailing address state USCA"}
                    value={(formData["Mailing address state USCA"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 500 }}>
                    <span style={{ fontWeight: 700 }}>Non U.S.:</span> State / Province as it
                    appears on form:
                  </label>
                  <FormInput
                    label={""}
                    id={"Mailing address state NonUS"}
                    name={"Mailing address state NonUS"}
                    value={(formData["Mailing address state NonUS"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <FormInput
                label={"Zip / Postal Code:"}
                id={"Mailing address zip"}
                name={"Mailing address zip"}
                value={(formData["Mailing address zip"] as string) || ""}
                onChange={handleChange}
              />
              <CountryDropdown
                label="Country:"
                name="Mailing address country"
                id="Mailing address country"
                value={
                  typeof formData["Mailing address country"] === "string"
                    ? formData["Mailing address country"]
                    : ""
                }
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "Mailing address country",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />
              {/* Yes/No radios */}
              <div style={{ marginTop: 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500 }}>Is care-of-address:</label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Mailing address is care of"
                      value="yes"
                      checked={formData["Mailing address is care of"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Mailing address is care of"
                      value="no"
                      checked={formData["Mailing address is care of"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500 }}>Is P.O. Box:</label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Mailing address is po box"
                      value="yes"
                      checked={formData["Mailing address is po box"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Mailing address is po box"
                      value="no"
                      checked={formData["Mailing address is po box"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500 }}>Is rural route:</label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Mailing address is rural route"
                      value="yes"
                      checked={formData["Mailing address is rural route"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Mailing address is rural route"
                      value="no"
                      checked={formData["Mailing address is rural route"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
              </div>
            </section>

            {/* Taxpayer identification section */}
            <section style={sectionStyle}>
              <FormTitle value="Taxpayer identification" />
              <FormInput
                label="Email:"
                id="Taxpayer email"
                name="Taxpayer email"
                value={(formData["Taxpayer email"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ marginBottom: 16 }}>
                <label
                  htmlFor="Taxpayer type"
                  style={{ fontWeight: 500, display: "block", marginBottom: 4 }}
                >
                  Type:
                </label>
                <select
                  id="Taxpayer type"
                  name="Taxpayer type"
                  value={
                    typeof formData["Taxpayer type"] === "string" ? formData["Taxpayer type"] : ""
                  }
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "Taxpayer type",
                        value: e.target.value,
                        type: "select-one",
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  style={{
                    width: "100%",
                    maxWidth: "100%",
                    padding: "8px",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                    boxSizing: "border-box",
                  }}
                >
                  <option value="" disabled>
                    ---Select---
                  </option>
                  <option value="SSN/ITIN">SSN/ITIN</option>
                </select>
              </div>
              <div style={{ marginBottom: 16 }}>
                <FormInput
                  label="U.S. taxpayer identification number:"
                  id="U.S. taxpayer identification number"
                  name="U.S. taxpayer identification number"
                  value={(formData["U.S. taxpayer identification number"] as string) || ""}
                  onChange={handleChange}
                  placeholder="XXX-XX-XXXX (SSN/ITIN format)"
                />
                {tinValidationMessage && (
                  <div
                    style={{
                      marginTop: 4,
                      padding: "6px 8px",
                      backgroundColor: tinValidationMessage.includes("✓") ? "#d4edda" : "#fff3cd",
                      border: `1px solid ${tinValidationMessage.includes("✓") ? "#28a745" : "#ffc107"}`,
                      borderRadius: 4,
                      fontSize: "12px",
                      color: tinValidationMessage.includes("✓") ? "#155724" : "#856404",
                    }}
                  >
                    {tinValidationMessage}
                  </div>
                )}
              </div>
              <FormInput
                label="Foreign tax identifying number:"
                id="Foreign tax identifying number"
                name="Foreign tax identifying number"
                value={(formData["Foreign tax identifying number"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ marginBottom: 16 }}>
                <input
                  type="checkbox"
                  id="W8BEN-FTIN_not_required"
                  name="W8BEN-FTIN_not_required"
                  checked={!!formData["W8BEN-FTIN_not_required"]}
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "W8BEN-FTIN_not_required",
                        type: "checkbox",
                        checked: e.target.checked,
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  style={{ marginRight: 8 }}
                />
                <label htmlFor="W8BEN-FTIN_not_required" style={{ fontWeight: 500 }}>
                  Check if FTIN not legally required :
                </label>
              </div>
              <FormInput
                label="Reference number:"
                id="W8BEN-Reference_numbers"
                name="W8BEN-Reference_numbers"
                value={(formData["W8BEN-Reference_numbers"] as string) || ""}
                onChange={handleChange}
              />
            </section>

            {/* Part II: Claim of Tax Treaty Benefits */}
            <section style={sectionStyle}>
              <FormTitle value="Part II Claim of Tax Treaty Benefits" />
              {treatyClaims.map((claim, idx) => (
                <div key={idx}>
                  <CountryDropdown
                    label="Claimed country:"
                    name="country"
                    id={`W8BEN-Treaty_country_${idx}`}
                    value={claim.country}
                    onChange={(e) => handleTreatyClaimChange(idx, e)}
                  />
                  <FormInput
                    label="Other:"
                    id={`Treaty_other_${idx}`}
                    name="other"
                    value={claim.other}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleTreatyClaimChange(idx, e)
                    }
                  />
                  <FormInput
                    label="Claimed Article:"
                    id={`W8BEN-Treaty_article_${idx}`}
                    name="article"
                    value={claim.article}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleTreatyClaimChange(idx, e)
                    }
                  />
                  <FormInput
                    label="Claimed Paragraph:"
                    id={`Treaty_paragraph_${idx}`}
                    name="paragraph"
                    value={claim.paragraph}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleTreatyClaimChange(idx, e)
                    }
                  />
                  <FormInput
                    label="Subparagraph Information (if any):"
                    id={`Treaty_subparagraph_${idx}`}
                    name="subparagraph"
                    value={claim.subparagraph}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleTreatyClaimChange(idx, e)
                    }
                  />
                  <FormInput
                    label="Claimed Percentage:"
                    id={`W8BEN-Treaty_withholding_rate_${idx}`}
                    name="percentage"
                    value={claim.percentage}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleTreatyClaimChange(idx, e)
                    }
                  />
                  <FormInput
                    label="Claimed type of income:"
                    id={`W8BEN-Treaty_income_type_${idx}`}
                    name="typeOfIncome"
                    value={claim.typeOfIncome}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleTreatyClaimChange(idx, e)
                    }
                  />
                  <FormInput
                    label="Claimed Explanation:"
                    id={`W8BEN-Treaty_conditions_explanation_${idx}`}
                    name="explanation"
                    value={claim.explanation}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleTreatyClaimChange(idx, e)
                    }
                  />
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddTreatyClaim}
                style={{
                  margin: "16px 0",
                  background: "#0094e8",
                  color: "white",
                  fontWeight: 600,
                  border: "none",
                  borderRadius: 2,
                  padding: "6px 12px",
                  cursor: "pointer",
                }}
              >
                ADD ANOTHER
              </button>
            </section>

            <section style={sectionStyle}>
              <FormTitle value={"Part III Certification"} />
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    id="Signature_capacity"
                    checked={formData.Signature_capacity}
                    onChange={(e) =>
                      handleChange({
                        target: {
                          name: "Signature_capacity",
                          type: "checkbox",
                          checked: e.target.checked,
                        },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                    style={{ marginRight: 8 }}
                  />
                  <label htmlFor="Signature_capacity" style={{ fontWeight: 500 }}>
                    I certify that I have the capacity to sign for the entity identified on line 1
                    of this form.
                  </label>
                </div>
                {showValidationErrors && errors["Signature_capacity"] && (
                  <div style={{ color: "red", fontSize: 12, marginTop: 4, marginBottom: 8 }}>
                    {errors["Signature_capacity"]}
                  </div>
                )}
                <div style={{ marginBottom: 8 }}>
                  <b>
                    Is signed: <span style={{ color: "red" }}>*</span>
                  </b>
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isSigned"
                      value="yes"
                      checked={formData.isSigned === "yes"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isSigned", value: "yes" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    Yes
                  </label>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isSigned"
                      value="no"
                      checked={formData.isSigned === "no"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isSigned", value: "no" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    No
                  </label>
                </div>
                {showValidationErrors && errors["isSigned"] && (
                  <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                    {errors["isSigned"]}
                  </div>
                )}
                <div style={{ marginBottom: 8 }}>
                  <b>
                    Has the form certification been modified:{" "}
                    <span style={{ color: "red" }}>*</span>
                  </b>
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isModified"
                      value="yes"
                      checked={formData.isModified === "yes"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isModified", value: "yes" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    Yes
                  </label>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isModified"
                      value="no"
                      checked={formData.isModified === "no"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isModified", value: "no" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    No
                  </label>
                </div>
                {showValidationErrors && errors["isModified"] && (
                  <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                    {errors["isModified"]}
                  </div>
                )}
                <FormInput
                  label={"Signed Name:"}
                  id={"Signer_printed_name"}
                  value={formData["Signer_printed_name"]}
                  name={"Signer_printed_name"}
                  onChange={handleChange}
                />

                <div style={{ marginBottom: "1rem" }}>
                  <FormDate
                    label="Date:"
                    name="Signature_date"
                    key="Signature_date"
                    value={formData["Signature_date"] || ""}
                    onChange={handleChange}
                  />
                </div>

                <div
                  style={{ display: "flex", alignItems: "center", marginBottom: 12, marginTop: 12 }}
                >
                  <input
                    type="checkbox"
                    id="Signature_unreadable"
                    checked={formData.Signature_unreadable || false}
                    onChange={(e) =>
                      handleChange({
                        target: {
                          name: "Signature_unreadable",
                          type: "checkbox",
                          checked: e.target.checked,
                        },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                    style={{ marginRight: 8 }}
                  />
                  <label htmlFor="Signature_unreadable" style={{ fontWeight: 500 }}>
                    Check if signature is unreadable
                  </label>
                </div>
              </div>
            </section>
            <div
              style={{
                display: "flex",
                marginTop: 20,
                marginBottom: 40,
                marginLeft: "auto",
                marginRight: "auto",
                width: "100%",
                maxWidth: "100%",
                alignItems: "center",
                boxSizing: "border-box",
              }}
            >
              <button
                type="submit"
                disabled={isSaving}
                style={{
                  padding: "6px 16px",
                  backgroundColor: InstaColors.primary,
                  color: "white",
                  fontWeight: 500,
                  marginLeft: "auto",
                  marginRight: "auto",
                  borderRadius: "0.375rem",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  cursor: "pointer",
                  border: "none",
                }}
              >
                {isSaving ? "Saving..." : "Save Form"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default W8BENFORM;
